import React, {useState} from 'react'
import moment from 'moment'
import {Badge} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {Tooltip} from 'react-tooltip'
import {useQuery} from '@apollo/client'
import {BadgeStatePolicy} from './BadgeStatePolicy'
import {ModalPreview} from '../../policies/components/ModalPreview'
import {
  getBadgeTextColor,
  getCarrierStatusVariant,
  getDocumentVariant,
  getPaymentVariant,
} from '../../../helpers/consts'
import {GET_ALL_POLICIES_STATUSES} from '../../../gql/queries/policiesQueries'
import {getColorByStatus, getTitleByStatus} from '../../shared/utils'
import {metalLevelLabel} from '../../../helpers/catalogMessages'

export interface FamilyItem {
  pk: number
  parentPk: number | null
  depth: number
  policyName: string | null
  status: string
  effectiveDate: string | null
  created: string | null
  updated: string | null
  agentName: string | null
  assignedToName: string | null
  holderName: string | null
  holderPhone: string | null
  stateCode: string | null
  zipCode: string | null
  carrierName: string | null
  planName: string | null
  policyTypeId: number | null
  policyTypeName: string | null
  metalLevel: number | null
  premium: string | null
  npn: string | null
  applicants: number | null
  isDuplicated: boolean | null
  stateDocs: string | null
  documentsDueDate: string | null
  paymentStatus: string | null
  carrierStatusGeneric: string | null
  carrierStatusRaw: string | null
  carrierIsAgeIn: boolean | null
  carrierIsDelinquent: boolean | null
}

interface Props {
  items: FamilyItem[]
  currentPk: number
  onNavigate: (pk: number) => void
}

function sortByHierarchy(items: FamilyItem[]): FamilyItem[] {
  const map = new Map<number, FamilyItem[]>()
  const roots: FamilyItem[] = []
  items.forEach((item) => {
    const parentExists = items.some((i) => i.pk === item.parentPk)
    if (item.parentPk === null || !parentExists) {
      roots.push(item)
    } else {
      const siblings = map.get(item.parentPk) ?? []
      siblings.push(item)
      map.set(item.parentPk, siblings)
    }
  })
  const result: FamilyItem[] = []
  function walk(nodes: FamilyItem[]) {
    nodes.sort((a, b) => a.pk - b.pk).forEach((node) => {
      result.push(node)
      walk(map.get(node.pk) ?? [])
    })
  }
  walk(roots)
  return result
}

const emDash = '—'
const hash = '#'
const treeBranch = '└'
const dollar = '$'
const zero = '0'
const sp = ' '
const yesMark = '✅'
const noMark = '❌'


export const PolicyFamilyTree: React.FC<Props> = ({items, currentPk, onNavigate}) => {
  const intl = useIntl()
  const t = (id: string, defaultMessage: string) => intl.formatMessage({id, defaultMessage})
  const nr = t('policies.list.preview.value.nr', 'N/R')
  const na = t('common.value.notAvailable', 'N/A')
  const sorted = sortByHierarchy(items)
  const [previewId, setPreviewId] = useState<string | null>(null)
  const {data: statusesData} = useQuery(GET_ALL_POLICIES_STATUSES, {fetchPolicy: 'cache-first'})
  const statuses = statusesData?.allPolicyStatuses?.edges ?? []

  return (
    <div className='table-responsive'>
      <table className='table table-hover table-sm align-middle mb-0' style={{whiteSpace: 'nowrap'}}>
        <thead className='table-light'>
          <tr>
            <th className='fw-bold'>{hash}</th>
            <th className='fw-bold'>
              <FormattedMessage id='policies.list.table.agent' defaultMessage='Agent' />
            </th>
            <th className='fw-bold'>
              <FormattedMessage id='policies.list.table.client' defaultMessage='Client' />
            </th>
            <th className='fw-bold'>
              <FormattedMessage id='policies.list.table.policy' defaultMessage='Policy' />
            </th>
            <th className='fw-bold'>
              <FormattedMessage id='policies.list.table.policyType' defaultMessage='Policy Type' />
            </th>
            <th className='fw-bold'>
              <FormattedMessage id='common.field.status' defaultMessage='Status' />
            </th>
            <th className='fw-bold'>
              <FormattedMessage id='policies.list.filters.field.assignedTo' defaultMessage='Assigned To' />
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((item) => {
            const isCurrent = item.pk === currentPk
            const tooltipId = `fam-tooltip-${item.pk}`
            const isDuplicateText = item.isDuplicated
              ? `${yesMark} ${t('common.boolean.yes', 'Yes')}`
              : `${noMark} ${t('common.boolean.no', 'No')}`
            return (
              <tr
                key={item.pk}
                style={{background: isCurrent ? 'rgba(var(--bs-primary-rgb), 0.06)' : undefined}}
              >
                {/* # + indentación */}
                <td>
                  <div
                    className='d-flex align-items-center gap-1'
                    style={{paddingLeft: `${item.depth * 18}px`}}
                  >
                    {item.depth > 0 && (
                      <span style={{color: '#bbb', fontSize: '0.75rem', lineHeight: 1}}>{treeBranch}</span>
                    )}
                    <span className='fw-semibold' style={{fontSize: '0.82rem'}}>
                      {hash}{item.pk}
                    </span>
                    {isCurrent && (
                      <Badge bg='warning' text='dark' style={{fontSize: '0.62rem', fontWeight: 600}}>
                        <FormattedMessage id='policies.detail.family.current' defaultMessage='current' />
                      </Badge>
                    )}
                  </div>
                </td>

                {/* Agent */}
                <td style={{fontSize: '0.82rem', maxWidth: '130px', wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.3'}}>
                  {item.agentName || emDash}
                </td>

                {/* Client (holder name + policy name + phone + state/zip) */}
                <td style={{maxWidth: '200px', minWidth: '160px', wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.3'}}>
                  <div className='fw-bold text-primary' style={{fontSize: '0.85rem'}}>
                    {item.policyName || item.holderName || emDash}
                  </div>
                  {item.holderPhone && (
                    <div style={{fontSize: '0.78rem', color: '#666'}}>
                      <i className='bi bi-telephone-fill me-1' />
                      {item.holderPhone}
                    </div>
                  )}
                  {(item.stateCode || item.zipCode) && (
                    <div style={{fontSize: '0.78rem', color: '#888'}}>
                      {item.stateCode} {item.zipCode}
                    </div>
                  )}
                </td>

                {/* Policy (carrier + effective + applicants) */}
                <td style={{maxWidth: '200px'}}>
                  <div className='info-policy-name'>
                    <span
                      className='text-primary fw-bold'
                      style={{cursor: 'default', fontSize: '0.85rem'}}
                      data-tooltip-id={tooltipId}
                    >
                      {item.carrierName || nr}
                    </span>
                    {item.effectiveDate && (
                      <p className='m-0 p-0' style={{fontSize: '0.78rem'}}>
                        <FormattedMessage
                          id='policies.list.page.cell.effective'
                          defaultMessage='Effective date: {date}'
                          values={{date: moment(item.effectiveDate).format('MMM DD, YYYY')}}
                        />
                      </p>
                    )}
                    <div className='d-flex align-items-center gap-1 mt-1 flex-wrap'>
                      <Badge
                        bg='primary'
                        className='d-flex align-items-center gap-1 text-white'
                        title={intl.formatMessage(
                          {
                            id: 'policies.list.page.titleApplicants',
                            defaultMessage: '{count, plural, one {# Applicant} other {# Applicants}}',
                          },
                          {count: item.applicants ?? 0}
                        )}
                        style={{fontSize: '0.7rem'}}
                      >
                        <i className='bi bi-people-fill' />
                        {item.applicants ?? 0}
                      </Badge>
                    </div>
                    <Tooltip
                      id={tooltipId}
                      clickable
                      delayHide={400}
                      place='top'
                      className='tooltip-tfi tooltip-clean'
                      style={{maxWidth: '320px'}}
                    >
                      <div className='tooltip-content' style={{whiteSpace: 'normal', wordBreak: 'break-word'}}>
                        <h6 className='text-center mb-2'>
                          <FormattedMessage
                            id='policies.detail.family.moreInfo'
                            defaultMessage='More information'
                          />
                        </h6>
                        <div>
                          <strong><FormattedMessage id='policies.list.page.ir.plan' defaultMessage='Plan:' /></strong> {item.planName || nr}
                        </div>
                        <div>
                          <strong><FormattedMessage id='policies.list.page.ir.metal' defaultMessage='Metal Level:' /></strong>{sp}
                          {metalLevelLabel(intl, item.metalLevel, na)}
                        </div>
                        <div>
                          <strong><FormattedMessage id='policies.list.page.ir.premium' defaultMessage='Premium:' /></strong> {dollar}{item.premium ?? zero}
                        </div>
                        <div>
                          <strong><FormattedMessage id='policies.list.page.ir.npn' defaultMessage='NPN:' /></strong> {item.npn || na}
                        </div>
                        <div>
                          <strong><FormattedMessage id='policies.list.page.ir.isDuplicate' defaultMessage='Is duplicate?' /></strong>{sp}
                          {isDuplicateText}
                        </div>
                        {item.created && (
                          <div>
                            <strong><FormattedMessage id='policies.list.page.ir.submitted' defaultMessage='Submitted:' /></strong> {moment(item.created).format('DD MMM, YY HH:mm')}
                          </div>
                        )}
                        {item.updated && (
                          <div>
                            <strong><FormattedMessage id='policies.list.page.ir.lastUpdate' defaultMessage='Last update:' /></strong> {moment(item.updated).format('DD MMM, YY HH:mm')}
                          </div>
                        )}
                      </div>
                    </Tooltip>
                  </div>
                </td>

                {/* Policy Type */}
                <td style={{fontSize: '0.82rem', maxWidth: '130px', wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.3'}}>
                  {item.policyTypeName || emDash}
                </td>

                {/* Status (policy + docs + payment + carrier) */}
                <td style={{minWidth: '140px'}}>
                  <BadgeStatePolicy
                    status={getTitleByStatus(statuses, item.status)}
                    color={getColorByStatus(statuses, item.status)}
                  />
                  <span className='d-block mt-1' style={{fontSize: '0.78rem'}}>
                    <FormattedMessage id='policies.list.page.cell.documents' defaultMessage='Documents:' />{sp}
                    <Badge className='badge-sm' bg={getDocumentVariant(item.stateDocs ?? undefined)} text={getBadgeTextColor(getDocumentVariant(item.stateDocs ?? undefined))}>
                      {item.stateDocs || na}
                    </Badge>
                  </span>
                  {item.documentsDueDate && (
                    <span className='d-block' style={{fontSize: '0.78rem'}}>
                      <FormattedMessage id='policies.detail.family.due' defaultMessage='Due date:' />{sp}
                      <Badge
                        className='badge-sm'
                        bg={moment(item.documentsDueDate).isBefore(moment(), 'day') ? 'danger' : 'warning'}
                        text={moment(item.documentsDueDate).isBefore(moment(), 'day') ? 'white' : 'dark'}
                      >
                        {moment(item.documentsDueDate).format('MMM DD, YY')}
                      </Badge>
                    </span>
                  )}
                  <span className='d-block' style={{fontSize: '0.78rem'}}>
                    <FormattedMessage id='policies.list.page.cell.payment' defaultMessage='Payment:' />{sp}
                    <Badge
                      className='badge-sm'
                      bg={getPaymentVariant(item.paymentStatus ?? undefined)}
                      text={getBadgeTextColor(getPaymentVariant(item.paymentStatus ?? undefined))}
                    >
                      {item.paymentStatus || na}
                    </Badge>
                  </span>
                  {item.carrierStatusGeneric && (
                    <span className='d-block' style={{fontSize: '0.78rem'}}>
                      <FormattedMessage id='policies.list.page.cell.carrier' defaultMessage='Carrier:' />{sp}
                      <Badge
                        className='badge-sm'
                        bg={getCarrierStatusVariant(item.carrierStatusGeneric)}
                        text={getBadgeTextColor(getCarrierStatusVariant(item.carrierStatusGeneric))}
                      >
                        {item.carrierStatusRaw || item.carrierStatusGeneric}
                      </Badge>
                      {item.carrierIsAgeIn && (
                        <Badge className='badge-sm ms-1' bg='info' text='white'>
                          <FormattedMessage id='policies.list.page.cell.ageIn' defaultMessage='AGE-IN' />
                        </Badge>
                      )}
                      {item.carrierIsDelinquent && item.carrierStatusGeneric !== 'DELINQUENT' && (
                        <Badge className='badge-sm ms-1' bg='warning' text='dark'>
                          <FormattedMessage id='policies.list.page.cell.dlq' defaultMessage='DLQ' />
                        </Badge>
                      )}
                    </span>
                  )}
                </td>

                {/* Assigned To */}
                <td style={{fontSize: '0.82rem', maxWidth: '130px', wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.3'}}>
                  {item.assignedToName || emDash}
                </td>

                {/* Preview */}
                <td>
                  <button
                    className={`btn btn-sm ${isCurrent ? 'btn-secondary' : 'btn-light-primary'}`}
                    style={{fontSize: '0.75rem', padding: '2px 8px'}}
                    disabled={isCurrent}
                    onClick={() => !isCurrent && setPreviewId(String(item.pk))}
                  >
                    <i className='bi bi-eye me-1' /> <FormattedMessage
                      id='policies.list.page.act.preview'
                      defaultMessage='Preview'
                    />
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {previewId && (
        <ModalPreview
          id={previewId}
          isOpen={!!previewId}
          onClose={() => setPreviewId(null)}
        />
      )}
    </div>
  )
}
